import { type ReactNode } from "react";

import type * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import {
  Accordion as ShadcnAccordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../shadcn/accordion";

// TODO: Missing array fields component

export enum AccordionVariant {
  CONTENT = "content",
  CONTENT_WITHOUT_BG = "content-without-border",
  FAQ = "faq",
  SELECT_CATEGORIES = "select-categories",
  WORKBENCH = "workbench",
}

export type AccordionItem = {
  title?: ReactNode | string;
  content?: ReactNode | string;
  value: string;
  variant?: AccordionVariant;
};

type Props = {
  variant?: AccordionVariant;
  items: AccordionItem[];
  isLoading?: boolean;
} & (
  | AccordionPrimitive.AccordionMultipleProps
  | AccordionPrimitive.AccordionSingleProps
);

export const Accordion = ({
  items,
  variant = AccordionVariant.CONTENT,
  isLoading,
  ...props
}: Props) => {
  const getAccordionStyles = (variant: AccordionVariant) => {
    switch (variant) {
      case AccordionVariant.CONTENT:
        return {
          header: "body-base-semibold text-text/primary",
          wrapper: "border-0 bg-background/secondary rounded-lg mb-4 last:mb-0",
          trigger: "hover:no-underline px-4",
          content: "body-small-regular text-text/primary px-4",
        };
      case AccordionVariant.CONTENT_WITHOUT_BG:
        return {
          header: "body-base-semibold text-text/primary w-full",
          wrapper: "border-0 rounded-lg mb-4 last:mb-0",
          trigger:
            "hover:no-underline px-4 bg-background/secondary rounded-t-lg",
          content:
            "body-small-regular text-text/primary p-4 bg-background/primary rounded-b-lg",
        };
      case AccordionVariant.FAQ:
        return {
          header: "text-left",
          wrapper: "data-[state=open]:border-b-0",
          trigger: "hover:no-underline px-4",
          content: "pb-0",
        };
      case AccordionVariant.SELECT_CATEGORIES:
        return {
          header: "",
          wrapper: "rounded-default overflow-hidden",
          trigger:
            "flex flex-row justify-between body-base-medium w-full items-center px-3 pt-4 pb-2 hover:bg-muted",
          content: "px-6",
        };
      case AccordionVariant.WORKBENCH:
        return {
          header: "body-base-semibold text-text/primary",
          wrapper:
            "border border-border/primary bg-background/primary rounded-lg mb-4 last:mb-0",
          trigger: "hover:no-underline px-4",
          content: "body-small-regular text-text/primary px-4",
        };
      default:
        return {
          header: "",
          wrapper: "",
          trigger: "",
          content: "",
        };
    }
  };

  return (
    <ShadcnAccordion {...props}>
      {items.map((item, index) => {
        const itemVariant = item.variant ?? variant;

        return (
          <AccordionItem
            className={getAccordionStyles(itemVariant).wrapper}
            defaultValue={props.defaultValue}
            key={item.value}
            value={item.value || String(index)}
          >
            <AccordionTrigger
              className={cnTwMerge(
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-default",
                getAccordionStyles(itemVariant).trigger,
              )}
            >
              <div
                className={cnTwMerge(getAccordionStyles(itemVariant).header, {
                  "skeleton-box": isLoading,
                })}
              >
                {item.title}
              </div>
            </AccordionTrigger>
            <AccordionContent
              className={getAccordionStyles(itemVariant).content}
            >
              {item.content}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </ShadcnAccordion>
  );
};
